// Reusable "Import test cases from Excel" panel — file upload, multi-sheet
// picker, rule-based + AI column mapping, editable mapping UI, and an import
// summary. Shared by the extension batch flow (ExtensionBatch.jsx) and the
// project detail page (ProjectDetails.jsx). It knows nothing about which
// backend it targets: the caller supplies a `fields` config plus `onDetect`
// and `onSubmit` adapters that shape the mapped rows into their own payload.
import { useRef, useState } from "react";
import {
  Upload, FileSpreadsheet, Loader2, Wand2, GitBranch, AlertTriangle,
} from "lucide-react";
import * as XLSX from "xlsx";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

const selectCls =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200";

// Parse a worksheet into row objects, but FIND THE REAL HEADER ROW first. Many exported test-case
// sheets have a merged TITLE row (e.g. "Test Cases – Login Page (Application: Mengo)") and/or a
// blank row above the actual column headers. SheetJS's default treats row 1 as the header, turning
// the real columns into "__EMPTY_1/2/3…" so nothing maps. We scan the first rows for the one that
// looks like a header (several cells + known header words) and re-parse from there.
const _HEADER_KW = /\b(id|steps?|expected|scenario|precondition|test\s*data|module|credential|url|name|title|priority|type|email|password|description|comment|actual|result)\b/i;
function rowsFromSheet(ws) {
  try {
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    let hdr = 0, best = -1;
    for (let i = 0; i < Math.min(aoa.length, 15); i++) {
      const row = aoa[i] || [];
      const nonEmpty = row.filter((c) => String(c).trim() !== "").length;
      const kw = row.filter((c) => _HEADER_KW.test(String(c))).length;
      const score = kw * 10 + nonEmpty;
      if (nonEmpty >= 3 && score > best) { best = score; hdr = i; }
    }
    return XLSX.utils.sheet_to_json(ws, { range: hdr, defval: "" });
  } catch (e) {
    return XLSX.utils.sheet_to_json(ws, { defval: "" });   // fallback to default behavior
  }
}

// Our own role-segmented export (Generated_Test_Cases.xlsx) opens on a Summary
// tab — a counts overview, not test cases. Re-importing that file must not offer
// it as a source sheet: its "header" (Sheet/Pages/Modules/Test Cases…) maps to
// nothing and picking it silently imports zero rows. Detected by name AND by the
// absence of a test-case-id-ish column, so a customer sheet that happens to be
// called "Summary" but does hold cases still comes through.
const _SUMMARY_NAME = /^\s*summary\s*$/i;
const _CASE_COL = /\b(test\s*case\s*id|tc\s*id|test\s*id|scenario|steps?)\b/i;
function isSummarySheet(name, rows) {
  if (!_SUMMARY_NAME.test(String(name))) return false;
  const cols = Object.keys(rows?.[0] || {});
  return !cols.some((c) => _CASE_COL.test(String(c)));
}

// Rule-based column guess (free) from each field's `aliases`. AI auto-detect is
// the fallback for cryptic headers.
function guessMapping(fields, columns) {
  const map = {};
  const lower = columns.map((c) => ({ raw: c, low: String(c).toLowerCase().trim() }));
  for (const { key, aliases = [] } of fields) {
    const hit =
      lower.find((c) => aliases.includes(c.low)) ||
      lower.find((c) => aliases.some((a) => c.low.includes(a)));
    if (hit) map[key] = hit.raw;
  }
  return map;
}

export default function ImportTestCasesPanel({
  fields,
  onDetect,
  onSubmit,
  onImported,
  showDefaultUrl = false,
  // Optional [{id, name}]. When given, the panel asks which project the sheet
  // belongs to and hands the choice back through onSubmit's context. That link
  // is the ONLY way an imported case's runs can later be attributed to a
  // project: cases pass it to their batch, the batch passes it to its runs.
  // Import without it and the Projects table reports 0 runs for a project that
  // has been tested all week.
  projects = null,
  // When true, the Import button stays disabled until the user has run
  // "AI auto-detect" at least once (forces a detect pass before importing).
  requireDetect = false,
}) {
  const fileRef = useRef(null);
  const [sheets, setSheets] = useState({});      // {sheetName: rows[]}
  const [sheet, setSheet] = useState("");
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaultUrl, setDefaultUrl] = useState("");
  const [projectId, setProjectId] = useState("");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(false); // has AI auto-detect been run for the current columns?
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  const rows = sheets[sheet] || [];
  // Import is blocked until a detect pass has run (only when the caller opts in
  // AND provides an onDetect adapter — otherwise there's nothing to gate on).
  const detectGate = requireDetect && !!onDetect && !detected;
  // Only gate when this panel is actually offering a project picker — a caller
  // that passes no `projects` list has no choice to make.
  const projectGate = Array.isArray(projects) && projects.length > 0 && !projectId;

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setSummary(null);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const all = {};
      for (const name of wb.SheetNames) {
        const parsed = rowsFromSheet(wb.Sheets[name]);
        if (isSummarySheet(name, parsed)) continue;   // overview tab, not cases
        all[name] = parsed;
      }
      const usable = Object.keys(all);
      if (!usable.length) {
        setErr("That file has no test-case sheets — only a Summary tab.");
        return;
      }
      const first = usable.find((n) => (all[n] || []).length) || usable[0];
      const cols = Object.keys(all[first]?.[0] || {});
      setSheets(all); setSheet(first); setColumns(cols);
      setMapping(guessMapping(fields, cols)); setFileName(file.name);
      setDetected(false); // new columns → require a fresh detect pass
    } catch (x) {
      setErr("Could not read that file. Use a .xlsx or .xls spreadsheet.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function pickSheet(name) {
    setSheet(name);
    const cols = Object.keys((sheets[name] || [])[0] || {});
    setColumns(cols); setMapping(guessMapping(fields, cols));
    setDetected(false); // switched sheet → require a fresh detect pass
  }

  async function aiDetect() {
    if (!columns.length || !onDetect) return;
    setDetecting(true); setErr(null);
    try {
      const res = await onDetect(columns, rows.slice(0, 3));
      // The backend answers 200 + {error} when the AI PROVIDER failed (no
      // credits / rejected key / rate limit). Show that reason — "unavailable"
      // hid which of those it was, so a broken key was undiagnosable.
      if (res?.error) setErr(res.error);
      else if (res && Object.keys(res).length) setMapping((m) => ({ ...m, ...res }));
      else setErr("AI couldn’t match any column — map them manually.");
    } catch (x) {
      setErr(x?.message || "AI detect failed — map the columns manually.");
    } finally {
      setDetecting(false);
      setDetected(true); // detect attempted → unlock import (even if it fell back to manual)
    }
  }

  async function doImport() {
    setBusy(true); setErr(null); setSummary(null);
    const val = (r, f) => (mapping[f] ? String(r[mapping[f]] ?? "").trim() : "");
    const mappedRows = rows.map((r) => {
      const out = {};
      for (const { key } of fields) out[key] = val(r, key);
      return out;
    });
    try {
      const res = await onSubmit(mappedRows, {
        fileName,
        defaultUrl: defaultUrl.trim() || null,
        projectId: projectId || null,
      });
      setSummary(res || {});
      await onImported?.();
    } catch (x) {
      setErr(x?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".xls,.xlsx" onChange={onFile} className="hidden" />
        <Button size="sm" onClick={() => fileRef.current?.click()}><Upload size={15} /> Choose .xlsx file</Button>
        {fileName && <span className="text-sm text-slate-500 dark:text-slate-400">{fileName}</span>}
        {Object.keys(sheets).length > 1 && (
          <select value={sheet} onChange={(e) => pickSheet(e.target.value)} className={selectCls + " max-w-[220px]"}>
            {Object.keys(sheets).map((n) => <option key={n} value={n}>{n} ({(sheets[n] || []).length})</option>)}
          </select>
        )}
      </div>

      {columns.length > 0 && (
        <>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Map your columns</p>
            {onDetect && (
              <Button size="sm" variant="outline" onClick={aiDetect} disabled={detecting}>
                {detecting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} AI auto-detect
              </Button>
            )}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {fields.map(({ key, label }) => (
              <label key={key} className="block">
                <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
                <select value={mapping[key] || ""} onChange={(e) => setMapping((m) => ({ ...m, [key]: e.target.value || undefined }))} className={selectCls}>
                  <option value="">— none —</option>
                  {columns.map((c) => <option key={c} value={c}>{c}{rows[0] && rows[0][c] !== "" ? ` (${String(rows[0][c]).slice(0, 20)})` : ""}</option>)}
                </select>
              </label>
            ))}
          </div>
          {showDefaultUrl && (
            <div className="mt-3">
              <Input label="Default URL (used when a row has no link)" icon={GitBranch}
                placeholder="https://your-app.com" value={defaultUrl} onChange={(e) => setDefaultUrl(e.target.value)} />
            </div>
          )}
          {Array.isArray(projects) && (
            <div className="mt-3">
              <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Project</span>
              {/* Required. This defaulted to "no project (ad-hoc)", and because a
                  default is what everyone leaves alone, EVERY case in the database
                  ended up unattributed — which is why every project reported 0 runs
                  and "Never" for last activity. The warning underneath was accurate
                  and ignored; a gate is the only thing that actually holds. */}
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={selectCls}>
                <option value="">— choose a project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="mt-1.5 text-xs text-slate-400">
                {projectId
                  ? "Runs from these cases will count towards this project's test cases, runs and bugs."
                  : "Pick the project these cases belong to. Without it their runs can never appear in any project's totals or reports."}
              </p>
            </div>
          )}
          {/* items-start + shrink-0: a provider reason ("no credits left — top it
              up…") wraps to two lines, which squashed a centred icon. */}
          {err && <p className="mt-3 flex items-start gap-1.5 rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"><AlertTriangle size={14} className="mt-px shrink-0" /> {err}</p>}
          {summary && (
            <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs font-medium text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200">
              Imported {summary.imported_count ?? 0}
              {summary.skipped != null ? ` · skipped ${summary.skipped}` : ""}
              {summary.duplicates != null ? ` · duplicates ${summary.duplicates}` : ""}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={doImport} disabled={busy || !rows.length || detectGate || projectGate}
              title={detectGate ? "Run AI auto-detect first"
                : projectGate ? "Choose the project these cases belong to" : undefined}>
              {busy ? <><Loader2 size={15} className="animate-spin" /> Importing…</> : <><FileSpreadsheet size={15} /> Import {rows.length} rows</>}
            </Button>
            {detectGate && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                <Wand2 size={13} /> Run <b>AI auto-detect</b> first to enable import.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
